import {useEffect, useRef, useState} from 'react'
import {useLocation} from 'react-router-dom'
import {msg, t, Trans} from '@lingui/macro'
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    MenuItem,
    Select,
    Snackbar,
    Stack,
    TextField,
    Typography,
    useTheme
} from '@mui/material'
import {
    GridActionsCellItem,
    GridRowEditStopReasons,
    GridRowModes,
    useGridApiContext,
    useGridApiRef
} from '@mui/x-data-grid'
import SaveIcon from '@mui/icons-material/Save'
import CancelIcon from '@mui/icons-material/Cancel'
import {Check, Visibility, VisibilityOff} from '@mui/icons-material'
import {useLingui} from '@lingui/react'
import SvgCustomIcon from "../icons/SvgCustomIcon";
import {StripedDataGrid} from "./StripedDataGrid";
import { formatDateForList } from '../../utils';

// Editable fields by user
// name
// surname
// department
// password
// is_verified

// Example user
// id: 1
// username: ""
// name: ""
// surname: ""
// organization: 0
// department: ""
// phone: "00000000"
// email: ""
// type: 1
// create_date: "2024-09-06"
// is_verified: false

const paginationModel = {page: 0, pageSize: 25}

/**
 * Sorts rows by the DataGrid sort model so we can freeze visual order during edit.
 * @param {Array} rows
 * @param {Array<{ field: string, sort: 'asc'|'desc' }>} sortModel
 * @returns {Array} New sorted array
 */
function sortRowsByModel(rows, sortModel) {
    if (!sortModel?.length) return rows
    return [...rows].sort((a, b) => {
        for (const {field, sort} of sortModel) {
            const aVal = a[field]
            const bVal = b[field]
            const aEmpty = aVal == null || aVal === ''
            const bEmpty = bVal == null || bVal === ''
            const cmp = aEmpty && bEmpty ? 0 : aEmpty ? 1 : bEmpty ? -1 : (aVal < bVal ? -1 : aVal > bVal ? 1 : 0)
            if (cmp !== 0) return sort === 'desc' ? -cmp : cmp
        }
        return 0
    })
}

function RoleEditCell({params, typeOptions, onOpenOrgDialog, onRoleChange}) {
    const apiRef = useGridApiContext()
    const {row} = params
    const value = typeof row.type === 'number' ? row.type : typeOptions.find(o => o.value === row.type)?.value ?? 0
    return (
        <Select
            value={value}
            onChange={(e) => {
                const v = Number(e.target.value)
                apiRef.current?.setEditCellValue?.({id: row.id, field: 'type', value: v})
                if (v === 3) {
                    onOpenOrgDialog({...row, type: v})
                } else {
                    onRoleChange?.(row.id, v)
                }
            }}
            size="small"
            fullWidth
            sx={{'& .MuiSelect-select': {py: 0.75}}}
        >
            {typeOptions.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                </MenuItem>
            ))}
        </Select>
    )
}

export default function UsersTable({usersRows, modifyUser, modifyUserPassword}) {
    const location = useLocation()
    const organizations = location.state?.init?.organizations || []
    const user = location.state?.init?.user || {}
    const [rows, setRows] = useState([])
    const [rowModesModel, setRowModesModel] = useState({})
    const [dialogOpen, setDialogOpen] = useState(false)
    const [userId, setUserId] = useState(0)
    const [selectedUser, setSelectedUser] = useState(null)
    const [orgIdsDialogOpen, setOrgIdsDialogOpen] = useState(false)
    const [orgIdsDialogRowId, setOrgIdsDialogRowId] = useState(null)
    const [orgIdsDialogSelected, setOrgIdsDialogSelected] = useState([])
    const [sortModel, setSortModel] = useState([])
    const apiRef = useGridApiRef()
    const theme = useTheme()
    const {_} = useLingui()

    let typeOptions = []
    if (user.type === 1) {
        typeOptions = [
            {label: t`User`, value: 0}
            , {label: t`Admin`, value: 1}
            , {label: t`Organization admin`, value: 2}
            , {label: t`Multi-organization admin`, value: 3}
        ]
    }
    if (user.type === 2) {
        typeOptions = [
            {label: t`User`, value: 0}
            , {label: t`Organization admin`, value: 2}
        ]
    }
    if (user.type === 3) {
        typeOptions = [
            {label: t`User`, value: 0}
            , {label: t`Organization admin`, value: 2}
            , {label: t`Multi-organization admin`, value: 3}
        ]
    }

    useEffect(() => {
        if (usersRows.length) setRows(usersRows)
    }, [usersRows])

    const handleRowEditStop = (params, event) => {
        if (params.reason === GridRowEditStopReasons.rowFocusOut) {
            event.defaultMuiPrevented = true
        }
    }

    const isEditing = Object.values(rowModesModel).some(m => m?.mode === GridRowModes.Edit)

    const handleEditClick = id => () => {
        // Freeze current order: sort rows by current sortModel so when we pass sortModel=[] during edit,
        // the table keeps the same visual order and the row won't jump when user changes role/other sorted field
        setRows(prev => sortRowsByModel(prev, sortModel))
        setRowModesModel({...rowModesModel, [id]: {mode: GridRowModes.Edit}})
    }

    const handleSaveClick = id => () => {
        setRowModesModel({...rowModesModel, [id]: {mode: GridRowModes.View}})
    }

    const handleCancelClick = id => () => {
        setRowModesModel({
            ...rowModesModel
            , [id]: {mode: GridRowModes.View, ignoreModifications: true}
        })

        const editedRow = rows.find(row => row.id === id)
        if (editedRow.isNew) {
            setRows(rows.filter(row => row.id !== id))
        }
    }

    const PasswordChangeDialog = ({id, selectedUser, open, onClose}) => {
        const passwordRef = useRef()
        const confirmPasswordRef = useRef()
        const [newPassword, setNewPassword] = useState('')
        const [confirmPassword, setConfirmPassword] = useState('')
        const [error, setError] = useState('')

        const handleMouseDownPassword = event => {
            event.preventDefault()
        }

        const handleSubmit = e => {
            e.preventDefault()

            // Basic validation
            if (newPassword !== confirmPassword) {
                setError(_(msg`Passwords must match`))
                return
            }

            if (newPassword.length < 8) {
                setError(_(msg`Password must be at least 8 characters long`))
                return
            }

            if (!/(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])./.test(newPassword)) {
                setError(_(msg`Password must contain at least a lowercase letter, an upper case letter and a digit`))
                return
            }

            // Call API to change the password
            selectedUser.password = newPassword
            modifyUserPassword({selectedUser, password: newPassword})

            // Reset form and close dialog
            setNewPassword('')
            setConfirmPassword('')
            setError('')
            onClose()
        }

        return (
            <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
                <DialogTitle sx={{color: theme.palette.newColors?.gray || theme.palette.text.primary}}>
                    <Trans>Change Password</Trans>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{mb: 2, color: theme.palette.text.secondary}}>
                        {_(msg`Enter and confirm the new password for this user.`)}
                    </Typography>
                    <form onSubmit={handleSubmit} id="password-change-form">
                        <Stack spacing={2}>
                            <TextField
                                inputRef={passwordRef}
                                label={_(msg`New Password`)}
                                value={newPassword}
                                onChange={e => setNewPassword(e.target.value)}
                                required
                                fullWidth
                                type={passwordRef.current?.type || 'password'}
                                InputProps={{
                                    endAdornment: <InputAdornment position='end'>
                                        <IconButton
                                            aria-label='toggle password visibility'
                                            onClick={() => {
                                                passwordRef.current.type === 'text'
                                                    ? (passwordRef.current.type = 'password')
                                                    : (passwordRef.current.type = 'text')
                                            }}
                                            onMouseDown={handleMouseDownPassword}
                                            edge='end'
                                        >
                                            {passwordRef.current?.type === 'text' ?
                                                <VisibilityOff color={theme.palette.newColors.gray}/> :
                                                <Visibility color={theme.palette.newColors.gray}/>}
                                        </IconButton>
                                    </InputAdornment>
                                }}
                            />
                            <TextField
                                inputRef={confirmPasswordRef}
                                label={_(msg`Confirm New Password`)}
                                value={confirmPassword}
                                onChange={e => setConfirmPassword(e.target.value)}
                                required
                                fullWidth
                                type={confirmPasswordRef.current?.type || 'password'}
                                InputProps={{
                                    endAdornment: <InputAdornment position='end'>
                                        <IconButton
                                            aria-label='toggle password visibility'
                                            onClick={() => {
                                                confirmPasswordRef.current.type === 'text'
                                                    ? confirmPasswordRef.current.type = 'password'
                                                    : confirmPasswordRef.current.type = 'text'
                                            }}
                                            onMouseDown={handleMouseDownPassword}
                                            edge='end'
                                        >
                                            {confirmPasswordRef.current?.type === 'text' ?
                                                <VisibilityOff color={theme.palette.newColors.gray}/> :
                                                <Visibility color={theme.palette.newColors.gray}/>}
                                        </IconButton>
                                    </InputAdornment>
                                }}
                            />
                            {error && (
                                <Typography color="error" variant="body2">
                                    {error}
                                </Typography>
                            )}
                        </Stack>
                    </form>
                </DialogContent>
                <DialogActions sx={{px: 3, pb: 2}}>
                    <Button
                        onClick={onClose}
                        sx={{color: theme.palette.newColors?.gray || theme.palette.text.secondary}}
                    >
                        <Trans>Cancel</Trans>
                    </Button>
                    <Button
                        type="submit"
                        form="password-change-form"
                        variant="contained"
                        sx={{
                            background: theme.palette.newColors?.cyanGradient || theme.palette.primary.main,
                            color: '#fff'
                        }}
                        onClick={handleSubmit}
                    >
                        <Trans>Change Password</Trans>
                    </Button>
                </DialogActions>
            </Dialog>
        )
    }

    const openOrgIdsDialog = (row) => {
        setOrgIdsDialogRowId(row.id)
        setOrgIdsDialogSelected(Array.isArray(row.organization_ids) ? row.organization_ids : [])
        setOrgIdsDialogOpen(true)
    }

    const handleRoleChange = (rowId, newType) => {
        if (newType !== 3) {
            setRows(rows.map(r => r.id === rowId ? {...r, type: newType, organization_ids: []} : r))
        }
    }

    const handleOrgIdsDialogConfirm = () => {
        if (orgIdsDialogRowId == null) return
        const updatedRows = rows.map(r =>
            r.id === orgIdsDialogRowId ? {...r, type: 3, organization_ids: orgIdsDialogSelected} : r
        )
        setRows(updatedRows)
        apiRef.current?.setEditCellValue?.({id: orgIdsDialogRowId, field: 'type', value: 3})
        setOrgIdsDialogOpen(false)
        setOrgIdsDialogRowId(null)
    }

    const handleOrgIdsDialogClose = () => {
        setOrgIdsDialogOpen(false)
        setOrgIdsDialogRowId(null)
    }

    const handleOrgCheckboxToggle = (orgValue) => {
        setOrgIdsDialogSelected(prev =>
            prev.includes(orgValue)
                ? prev.filter(v => v !== orgValue)
                : [...prev, orgValue]
        )
    }

    const availableOrgs = (Array.isArray(organizations) ? organizations : []).filter(org => org.value > 0)

    const handleOpenDialog = (id, selectedUser) => () => {
        setUserId(id)
        setSelectedUser(selectedUser)
        setDialogOpen(true)
    }

    const handleCloseDialog = () => setDialogOpen(false)

    const processRowUpdate = (newRow, oldRow) => {
        let updatedRow = {isNew: false, id: newRow.id}
        Object.keys(newRow).forEach(key => {
            if (newRow[key] !== oldRow[key]) {
                updatedRow = {...updatedRow, [key]: newRow[key]}
            }
        })
        const modifiedUser = Object.assign(oldRow, newRow)
        if (modifiedUser.type === 3) {
            modifiedUser.organization_ids = Array.isArray(modifiedUser.organization_ids) && modifiedUser.organization_ids.length
                ? modifiedUser.organization_ids
                : (rows.find(r => r.id === newRow.id)?.organization_ids ?? [])
        }
        modifyUser(modifiedUser)
        setRows(rows.map(row => (row.id === newRow.id ? newRow : row)))
        return updatedRow
    }

    const handleRowModesModelChange = newRowModesModel => {
        setRowModesModel(newRowModesModel)
    }

    const columns = [
        {
            field: 'id'
            , headerName: t`ID`
            , headerClassName: 'overload-theme--header'
            , flex: 0.5
        }
        , {
            field: 'username'
            , headerName: t`Username`
            , headerClassName: 'overload-theme--header'
            , flex: 1.5
        }
        , {
            field: 'name'
            , headerName: t`First name`
            , editable: true
            , headerClassName: 'overload-theme--header'
            , flex: 1
        }
        , {
            field: 'surname'
            , headerName: t`Last name`
            , editable: true
            , headerClassName: 'overload-theme--header'
            , flex: 1
        }
        , {
            field: 'organization'
            , headerName: t`Organization`
            , headerClassName: 'overload-theme--header'
            , flex: 1
            , valueGetter: value => {
                if (value) {
                    return organizations.find(o => o.value === value)?.label
                }
            }
        }
        , {
            field: 'departmentPol'
            , headerName: t`Department/Unit`
            , headerClassName: 'overload-theme--header'
            , editable: true
            , flex: 1.25
            , valueGetter: (_, row) => row.organization === 1 ? row.department : null
            , valueSetter: (value, row) => ({...row, department: value || row.department})
        }
        , {
            field: 'departmentHos'
            , headerName: t`Hospital/Healthcare facility`
            , headerClassName: 'overload-theme--header'
            , editable: true
            , flex: 1.25
            , valueGetter: (_, row) => row.organization === 2 ? row.department : null
            , valueSetter: (value, row) => ({...row, department: value || row.department})
        }
        , {
            field: 'departmentIns'
            , headerName: t`Company`
            , headerClassName: 'overload-theme--header'
            , editable: true
            , flex: 1.25
            , valueGetter: (_, row) => row.organization === 3 ? row.department : null
            , valueSetter: (value, row) => ({...row, department: value || row.department})
        }
        , {
            field: 'phone'
            , headerName: t`Phone`
            , editable: true
            , headerClassName: 'overload-theme--header'
            , flex: 1
        }
        , {
            field: 'email'
            , headerName: t`Email`
            , editable: true
            , headerClassName: 'overload-theme--header'
            , flex: 1
        }
        , {
            field: 'create_date'
            , headerName: t`Date created`
            , headerClassName: 'overload-theme--header'
            , flex: 1
            , valueFormatter: (paramsOrValue) => formatDateForList(paramsOrValue?.value ?? paramsOrValue)
        }
        , {
            field: 'type'
            , headerName: t`Role`
            , type: 'singleSelect'
            , editable: true
            , headerClassName: 'overload-theme--header'
            , flex: 1
            , valueOptions: typeOptions
            , renderCell: (params) => {
                const roleLabel = typeOptions.find(o => o.value === params.value)?.label ?? params.value
                return (
                    <span>{roleLabel}</span>
                )
            }
            , renderEditCell: (params) => (
                <RoleEditCell
                    params={params}
                    typeOptions={typeOptions}
                    onOpenOrgDialog={openOrgIdsDialog}
                    onRoleChange={handleRoleChange}
                />
            )
        }
        , {
            field: 'user_organizations'
            , headerName: t`Organizations`
            , editable: true
            , headerClassName: 'overload-theme--header'
            , flex: 2
            , renderCell: (params) => {
                const role = params.row.type;
                if (role !== 3) return <></>
                const orgsLabel = params.row.user_organizations_string;
                return (
                    <span>{orgsLabel}</span>
                )
            }
            , renderEditCell: (params) => {
                const role = params.row.type;
                if (role !== 3) return <></>
                return (
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => openOrgIdsDialog(params.row)}
                        sx={{
                            color: theme.palette.newColors?.cyan || theme.palette.primary.main,
                            borderColor: theme.palette.newColors?.cyan || theme.palette.primary.main
                        }}
                    >
                        {_(msg`Select Organizations`)}
                    </Button>
                )
            }
        }
        , {
            field: 'verified'
            , headerName: t`Verified`
            , type: 'boolean'
            , editable: true
            , headerClassName: 'overload-theme--header'
            , flex: 0.75
            , renderCell: (params) => {
                const isVerified = params.value
                const color = theme.palette.mode === 'dark'
                    ? (isVerified ? '#4caf50' : '#f44336')
                    : (isVerified ? '#2e7d32' : '#d32f2f')

                return isVerified
                    ? <Check sx={{color}}/>
                    : <CancelIcon sx={{color}}/>
            }
        }
        , {
            field: 'actions'
            , type: 'actions'
            , headerName: t`Actions`
            , headerClassName: 'overload-theme--header'
            , flex: 1
            , cellClassName: 'actions'
            , getActions: ({id}) => {
                const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit

                if (isInEditMode) {
                    return [
                        <GridActionsCellItem
                            icon={<SvgCustomIcon name={'save'}
                                                  color={theme.palette.newColors.gray}/>}
                            label={t`Save`}
                            sx={{
                                color: 'primary.main'
                            }}
                            onClick={handleSaveClick(id)}
                        />
                        , <GridActionsCellItem
                            icon={<SvgCustomIcon name={'cancel'}
                                                  color={theme.palette.newColors.gray}/>}
                            label={t`Cancel`}
                            className="textPrimary"
                            onClick={handleCancelClick(id)}
                            color="inherit"
                        />
                    ]
                }

                return [
                    <GridActionsCellItem
                        icon={<SvgCustomIcon color={theme.palette.newColors.gray}
                                              name={'edit'}/>}
                        label={t`Edit`}
                        className="textPrimary"
                        onClick={handleEditClick(id)}
                        color="inherit"
                    />
                    , <GridActionsCellItem
                        icon={<SvgCustomIcon color={theme.palette.newColors.gray}
                                              name={'key'}/>}
                        label={t`Change password`}
                        className="textPrimary"
                        onClick={() => {
                            const selUser = usersRows.find(u => u.id === id)
                            handleOpenDialog(id, selUser)()
                        }}
                        color="inherit"
                    />
                ]
            }
        }
    ]

    return (
        <Box
            width='100%'
            sx={theme => ({
                '& .overload-theme--header': {
                    backgroundColor: theme.palette.primary.main
                    , color: theme.palette.primary.contrastText
                }
            })}
        >
            <StripedDataGrid
                apiRef={apiRef}
                sortModel={isEditing ? [] : sortModel}
                onSortModelChange={model => {
                    if (!isEditing) setSortModel(model)
                }}
                showColumnVerticalBorder
                showCellVerticalBorder
                rows={rows}
                columns={columns.filter(col => col.field !== 'user_organizations' || user.type === 1)}
                editMode="row"
                isCellEditable={params => {
                    // Users cannot edit their own role (type field)
                    if (params.field === 'type' && params.row.id === user.id) return false
                    // Durante la modifica la cella Role resta editabile così si può cambiare di nuovo (es. da Admin a User)
                    if (rowModesModel[params.row.id]?.mode === GridRowModes.Edit) return true
                    // Admin users (type === 1) non editabili quando in view
                    if (params.row.type === 1) return false
                    return true
                }}
                rowModesModel={rowModesModel}
                onRowModesModelChange={handleRowModesModelChange}
                onRowEditStop={handleRowEditStop}
                processRowUpdate={processRowUpdate}
                onProcessRowUpdateError={error => error}
                initialState={{pagination: {paginationModel}}}
                pageSizeOptions={[10, 25, 50]}
                getRowClassName={params => {
                    return params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
                }}
            />
            <PasswordChangeDialog selectedUser={selectedUser} id={userId} open={dialogOpen}
                                  onClose={handleCloseDialog}/>
            <Dialog open={orgIdsDialogOpen} onClose={handleOrgIdsDialogClose} maxWidth="xs" fullWidth>
                <DialogTitle sx={{color: theme.palette.newColors?.gray || theme.palette.text.primary}}>
                    {_(msg`Organizations for Multi-organization admin`)}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{mb: 1, color: theme.palette.text.secondary}}>
                        {_(msg`Select the organizations this user can control and view.`)}
                    </Typography>
                    <List dense disablePadding>
                        {availableOrgs.map(org => (
                            <ListItem key={org.value} disablePadding sx={{py: 0.25}}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={orgIdsDialogSelected.includes(org.value)}
                                            onChange={() => handleOrgCheckboxToggle(org.value)}
                                            sx={{
                                                color: theme.palette.newColors?.gray,
                                                '&.Mui-checked': {color: theme.palette.newColors?.cyan || theme.palette.primary.main}
                                            }}
                                        />
                                    }
                                    label={<Typography variant="body2">{org.label}</Typography>}
                                />
                            </ListItem>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions sx={{px: 3, pb: 2}}>
                    <Button
                        onClick={handleOrgIdsDialogClose}
                        sx={{color: theme.palette.newColors?.gray || theme.palette.text.secondary}}
                    >
                        <Trans>Cancel</Trans>
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleOrgIdsDialogConfirm}
                        sx={{
                            background: theme.palette.newColors?.cyanGradient || theme.palette.primary.main,
                            color: '#fff'
                        }}
                    >
                        <Trans>Confirm</Trans>
                    </Button>
                </DialogActions>
            </Dialog>
            {(() => {
                const editingIds = Object.entries(rowModesModel)
                    .filter(([, m]) => m?.mode === GridRowModes.Edit)
                    .map(([id]) => Number(id))
                const multiOrgRows = editingIds
                    .map(id => rows.find(r => r.id === id))
                    .filter(r => r?.type === 3 && Array.isArray(r.organization_ids) && r.organization_ids.length > 0)
                const showOrgSnackbar = multiOrgRows.length > 0
                return (
                    <Snackbar
                        open={showOrgSnackbar}
                        anchorOrigin={{vertical: 'top', horizontal: 'center'}}
                        message={
                            showOrgSnackbar ? (
                                <Stack component="span" direction="column" spacing={0.5}>
                                    {multiOrgRows.map(row => {
                                        const orgLabels = (row.organization_ids || [])
                                            .map(id => organizations.find(o => o.value === id)?.label)
                                            .filter(Boolean)
                                        return (
                                            <Typography key={row.id} variant="body2" component="span" display="block">
                                                <strong>{row.username || row.name || _(msg`User`)}</strong>, {_(msg`organizations selected`)}: {orgLabels.join(', ')}
                                            </Typography>
                                        )
                                    })}
                                </Stack>
                            ) : ''
                        }
                    />
                )
            })()}
        </Box>
    )
}
